import { persistentReducedStream, reducer } from '@ardoq/rxbeach';
import { Observable, distinctUntilChanged, map } from 'rxjs';
import { buildInitialStreamState } from '../utils';
import { IntegrationId } from '../tabularMappings/types';
import {
  AllSelectionState,
  FetchSelectionPayload,
  FetchSelectionSuccessPayload,
  SelectionStateData,
} from './types';
import {
  fetchSelection,
  fetchSelectionError,
  fetchSelectionSuccess,
} from './actions';
import { resetIntegration } from '../activeIntegrations/actions';
import { WithIntegrationId } from 'integrations/common/utils/actionCreatorWithIntegrationId';
import { normalizeSelectionResponse } from './utils';

const initialInstanceState: SelectionStateData = { status: 'INIT' };

const initialState: AllSelectionState =
  buildInitialStreamState<SelectionStateData>(() => initialInstanceState);

const fetchSelectionReducer = (
  state: AllSelectionState,
  { integrationId }: FetchSelectionPayload
): AllSelectionState => ({
  ...state,
  [integrationId]: { status: 'LOADING' },
});

const fetchSelectionSuccessReducer = (
  state: AllSelectionState,
  { integrationId, response }: FetchSelectionSuccessPayload
): AllSelectionState => ({
  ...state,
  [integrationId]: {
    status: 'SUCCESS',
    data: normalizeSelectionResponse(response),
  },
});

const fetchSelectionErrorReducer = (
  state: AllSelectionState,
  { integrationId }: WithIntegrationId
): AllSelectionState => ({
  ...state,
  [integrationId]: { status: 'FAILURE' },
});

const resetIntegrationReducer = (
  state: AllSelectionState,
  integrationId: IntegrationId
): AllSelectionState => ({
  ...state,
  [integrationId]: initialInstanceState,
});

const reducers = [
  reducer(fetchSelection, fetchSelectionReducer),
  reducer(fetchSelectionSuccess, fetchSelectionSuccessReducer),
  reducer(fetchSelectionError, fetchSelectionErrorReducer),
  reducer(resetIntegration, resetIntegrationReducer),
];

export const selectionState$ = persistentReducedStream(
  'selectionState$',
  initialState,
  reducers
);

const isSelectionStateEqual = (
  a: SelectionStateData,
  b: SelectionStateData
): boolean => {
  if (a.status !== b.status) return false;
  if (a.status === 'SUCCESS' && b.status === 'SUCCESS') {
    return a.data === b.data;
  }
  return true;
};

// Memoized stream getter
const integrationStreams = new Map<
  IntegrationId,
  Observable<SelectionStateData>
>();

export const getSelectionStateStream = (
  integrationId: IntegrationId
): Observable<SelectionStateData> => {
  const existing = integrationStreams.get(integrationId);
  if (existing) return existing;

  const stream$ = selectionState$.pipe(
    map(state => state[integrationId]),
    distinctUntilChanged(isSelectionStateEqual)
  );
  integrationStreams.set(integrationId, stream$);
  return stream$;
};
